import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import FancyTimeStamp from './FancyTimeStamp';
import { useUserPresence } from '../hooks/useUserPresence';
import { useMatrixClient } from '../context/MatrixContext';
import { useI18n } from '../context/I18nContext';
import { useDisambiguation } from '../context/DisambiguationContext';
import { getPresenceColor, getPresenceTitle } from '../utils/presenceUtils';
import './ContactList.css';

const ContactListItem = ({
  name = '',
  avatar = '',
  organization = '',
  lastActivityTimestamp = null,
  isSelected = false,
  isToggleItem = false,
  isToggled = false,
  onClick = () => {},
  presence = null,
  online = false,
  disabled = false,
  powerLevel = 0,
  matrixId = '',
  shouldShowMatrixId = false,
  isCurrentUser = false,
  membership = 'join',
}) => {
  const { t } = useI18n();
  const [isHovered, setIsHovered] = useState(false);
  const presenceRecord = presence;
  const isOnline = online;

  const highlightState = (isSelected)
    ? 'selected'
    : ((isHovered && !disabled) ? 'hovered' : '');

  return (
    <div
      className={`contact-list-item ${highlightState} ${disabled ? 'disabled' : ''}`}
      data-mxid={matrixId}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => !disabled && setIsHovered(false)}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
    >
      {
        (!isToggleItem || !isToggled) && <div className="contact-list-item-avatar">
          <img src={avatar} alt="Avatar" crossOrigin="anonymous" />
          <div
            className="contact-list-item-presence-indicator"
            style={{ backgroundColor: getPresenceColor(presenceRecord, isOnline) }}
            title={getPresenceTitle(presenceRecord, isOnline)}
          />
        </div>
      }
      {
        (isToggleItem && isToggled) && <div className="contact-list-item-check">
          <span className="material-icons">check</span>
        </div>
      }
      <div className="contact-list-item-info">
        <div className="contact-list-item-name">
          {name} {isCurrentUser && `(${t('you')})`}
        </div>
        {
          shouldShowMatrixId && matrixId && <div className="contact-list-item-matrix-id">{matrixId}</div>
        }
        {
          membership === 'invite' && <div className="contact-list-item-admin">{t('ContainerListView.invited')}</div>
        }
        {
          membership !== 'invite' && powerLevel >= 100 && <div className="contact-list-item-admin">{t('ContainerListView.admin')}</div>
        }
        {
          membership !== 'invite' && powerLevel >= 50 && powerLevel < 100 && <div className="contact-list-item-admin">{t('ContainerListView.moderator')}</div>
        }
        {
          organization && <div className="contact-list-item-description">{organization}</div>
        }
      </div>
      {
        lastActivityTimestamp && (
          <div className="contact-list-item-timestamp">
            <FancyTimeStamp timestamp={lastActivityTimestamp} />
          </div>
        )
      }
    </div>
  );
};

ContactListItem.propTypes = {
  name: PropTypes.string,
  avatar: PropTypes.string,
  organization: PropTypes.string,
  lastActivityTimestamp: PropTypes.number,
  isSelected: PropTypes.bool,
  isToggleItem: PropTypes.bool,
  isToggled: PropTypes.bool,
  onClick: PropTypes.func,
  presence: PropTypes.shape({
    presence: PropTypes.string,
    currentlyActive: PropTypes.bool,
    lastActiveAgo: PropTypes.number,
    statusMsg: PropTypes.string,
    avatarUrl: PropTypes.string,
  }),
  online: PropTypes.bool,
  disabled: PropTypes.bool,
  powerLevel: PropTypes.number,
  matrixId: PropTypes.string,
  shouldShowMatrixId: PropTypes.bool,
  isCurrentUser: PropTypes.bool,
  membership: PropTypes.string,
};

const ContactList = ({
  contacts = [],
  selectedContactId = '',
  emptyText = '',
  onSelectContact = () => {},
  isToggleList = false,
  isToggled = (_id) => false,
  disabled = (_id) => false,
}) => {
  const { getPresence, isOnline } = useUserPresence();
  const client = useMatrixClient();
  const { needsDisambiguation } = useDisambiguation();
  const myUserId = client?.getUserId();

  // Build local set of contacts with duplicate display names in this list
  // This catches duplicates not known to the global DisambiguationContext
  // (e.g. search results from users not in any shared room)
  const localDuplicateIds = useMemo(() => {
    const nameToIds = new Map();
    for (const contact of contacts) {
      const name = (contact.rawDisplayName || contact.name || '').toLowerCase().trim();
      if (!name) continue;
      const existing = nameToIds.get(name);
      if (existing) {
        existing.push(contact.id);
      } else {
        nameToIds.set(name, [contact.id]);
      }
    }
    const dupes = new Set();
    for (const ids of nameToIds.values()) {
      if (ids.length > 1) {
        ids.forEach(id => dupes.add(id));
      }
    }
    return dupes;
  }, [contacts]);

  return (
    <div className="contact-list">
      {
        contacts.length > 0 ? (
          contacts.map((contact) => {
            // Call getPresence and isOnline directly to avoid stale closure issues
            // Previously using useMemo caused callbacks to capture old presenceMap state
            const presence = getPresence(contact.id);
            const online = isOnline(contact.id);

            // Prefer rawDisplayName (clean) over name (may have SDK-added Matrix ID suffix)
            // This ensures duplicate detection works correctly for both User and RoomMember objects
            const displayName = contact.rawDisplayName || contact.name;

            // Show Matrix ID when global disambiguation context OR local list detects duplicate display names
            // Skip MXID for current user when (You) suffix is shown (takes priority)
            const isCurrentUser = contact.id === myUserId;
            const shouldShowMxId = !isCurrentUser && (needsDisambiguation(contact.id) || localDuplicateIds.has(contact.id));

            return (
              <ContactListItem
                key={contact.id}
                name={displayName}
                avatar={contact.avatar}
                organization={contact.organization}
                lastActivityTimestamp={contact.lastActivityTimestamp}
                isSelected={selectedContactId === contact.id}
                isToggled={isToggled(contact.id)}
                isToggleItem={isToggleList}
                onClick={() => onSelectContact(contact.id)}
                presence={presence}
                online={online}
                disabled={disabled(contact.id)}
                powerLevel={contact.powerLevel || 0}
                matrixId={contact.id}
                shouldShowMatrixId={shouldShowMxId}
                isCurrentUser={isCurrentUser}
                membership={contact.membership || 'join'}
              />
            );
          })
        ) : (
          <div className="contact-list-empty-text">
            {emptyText}
          </div>
        )
      }
    </div>
  );
};

ContactList.propTypes = {
  contacts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    avatar: PropTypes.string,
    organization: PropTypes.string,
    lastActivityTimestamp: PropTypes.number,
    powerLevel: PropTypes.number,
    membership: PropTypes.string,
  })),
  selectedContactId: PropTypes.string,
  emptyText: PropTypes.string,
  onSelectContact: PropTypes.func,
  isToggleList: PropTypes.bool,
  isToggled: PropTypes.func,
  disabled: PropTypes.func,
};

export default ContactList;
